import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { NoteComment } from '@/types/note';

interface SaveHandlerProps {
  title: string;
  content: string;
  color: string;
  coverImage: string | null;
  selectedGroup: string;
  noteId: string | null;
  comments: NoteComment[];
}

export const useSaveHandler = ({
  title,
  content,
  color,
  coverImage,
  selectedGroup,
  noteId,
  comments,
}: SaveHandlerProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const liveBlockNoteContent = typeof window.getBlockNoteMarkdown === 'function'
      ? await window.getBlockNoteMarkdown().catch(() => undefined)
      : undefined;
    const latestBlockNoteContent = liveBlockNoteContent ?? window.__mindnoteLatestBlockNoteMarkdown;
    const contentToSave = typeof latestBlockNoteContent === 'string' ? latestBlockNoteContent : content;

    if (!title.trim() || !contentToSave.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide both title and content for your note.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        throw new Error('Not authenticated');
      }

      const noteData = {
        title,
        content: contentToSave,
        color,
        cover_image: coverImage,
        group_id: selectedGroup === "none" || !selectedGroup ? null : selectedGroup,
        user_id: user.id,
        updated_at: new Date().toISOString()
      };

      let savedNote: any = null;

      if (noteId) {
        // Update existing note - separate update from select to avoid timeout
        const { error: updateError } = await supabase
          .from('notes')
          .update({
            title: noteData.title,
            content: noteData.content,
            color: noteData.color,
            cover_image: noteData.cover_image,
            group_id: noteData.group_id,
            updated_at: noteData.updated_at
          })
          .eq('id', noteId)
          .eq('user_id', user.id);

        if (updateError) {
          console.error('[SaveHandler] Update error:', updateError);
          throw updateError;
        }

        // Build the saved note from known data
        savedNote = { id: noteId, ...noteData };
        console.log('[SaveHandler] Update succeeded for note:', noteId);
      } else {
        // Create new note - try with .select() first, fall back to insert-only
        const { data, error: insertError } = await supabase
          .from('notes')
          .insert([{
            title: noteData.title,
            content: noteData.content,
            color: noteData.color,
            cover_image: noteData.cover_image,
            group_id: noteData.group_id,
            user_id: noteData.user_id
          }])
          .select()
          .single();

        if (insertError) {
          // If the error is a timeout, the insert may have succeeded
          if (insertError.code === '57014' || insertError.message?.includes('timeout')) {
            console.warn('[SaveHandler] Insert timed out but may have succeeded');
            savedNote = { id: crypto.randomUUID(), ...noteData, created_at: new Date().toISOString() };
          } else {
            console.error('[SaveHandler] Insert error:', insertError);
            throw insertError;
          }
        } else {
          savedNote = data;
          console.log('[SaveHandler] Insert succeeded, note id:', data?.id);
        }
      }

      // Cache the saved note in sessionStorage so Home can show it immediately
      try {
        if (savedNote) {
          const cacheKey = `cached_note_${savedNote.id}`;
          sessionStorage.setItem(cacheKey, JSON.stringify(savedNote));
        }
      } catch (e) {
        console.warn('Failed to cache saved note', e);
      }

      // Persist comments locally by note id
      try {
        const savedId = noteId || savedNote?.id;
        if (savedId) {
          localStorage.setItem(`note_comments_${savedId}`, JSON.stringify(comments));
        }
      } catch (e) {
        console.warn('Failed to persist comments after save', e);
      }

      // Migrate AI-context links from the 'new' bucket to the saved note id
      // so that links added before the first save remain usable as context.
      try {
        const savedId = savedNote?.id;
        if (!noteId && savedId) {
          const newKey = 'note_ai_context_urls_new';
          const targetKey = `note_ai_context_urls_${savedId}`;
          const pending = JSON.parse(localStorage.getItem(newKey) ?? '[]');
          if (Array.isArray(pending) && pending.length > 0) {
            const existing = JSON.parse(localStorage.getItem(targetKey) ?? '[]');
            const merged = Array.from(new Set([
              ...(Array.isArray(existing) ? existing : []),
              ...pending.filter((u: unknown) => typeof u === 'string'),
            ]));
            localStorage.setItem(targetKey, JSON.stringify(merged));
            localStorage.removeItem(newKey);
          }
        }
      } catch (e) {
        console.warn('Failed to migrate AI-context links after save', e);
      }


      toast({
        title: noteId ? "Note updated" : "Note created",
        description: noteId ? "Your note has been updated successfully." : "Your note has been created successfully.",
      });

      navigate('/');
    } catch (error: any) {
      console.error('[SaveHandler] Error saving note:', error, 'user_id used:', (await supabase.auth.getSession()).data.session?.user?.id);
      toast({
        title: "Error saving note",
        description: error.message || "Failed to save your note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return { handleSave, isSaving };
};
